import type { ColumnDef, Table } from '@tanstack/react-table'
import { useState } from 'react'

import { AddFilterButton, Filters, Search, TableExport } from '@/components'
import { DataTable, type DataTableColumns } from '@/components/ui'
import {
  type FieldSchema,
  FiltersProvider,
  useFilters,
  useSearchQuery,
} from '@/stores'

interface PortfolioTableProps<Row> {
  // Own filter store per tab so each tab keeps its own facets (persisted by contextId), the same way
  // the Transactions page owns one. Account scope lives above this, in the accounts rollup.
  contextId: string
  dialogId: string
  columns: ColumnDef<Row, unknown>[]
  data: Row[]
  schema: FieldSchema[]
  getUniqueValues: (field: string) => string[]
  exportFilename: string
  paginationKey: string
  emptyMessage: string
}

const PortfolioTableInner = <Row,>({
  dialogId,
  columns,
  data,
  schema,
  getUniqueValues,
  exportFilename,
  paginationKey,
  emptyMessage,
}: Omit<PortfolioTableProps<Row>, 'contextId'>) => {
  const [table, setTable] = useState<Table<Row> | null>(null)
  const filters = useFilters()
  const searchQuery = useSearchQuery()

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Filters
          dialogId={dialogId}
          schema={schema}
          getUniqueValues={getUniqueValues}
        />
        <div className="flex items-center gap-2">
          <Search />
          <AddFilterButton dialogId={dialogId} schema={schema} />
          {table && <TableExport table={table} filename={exportFilename} />}
        </div>
      </div>
      <div className="flex-1 min-h-[420px] flex flex-col rounded-lg border bg-white overflow-auto max-h-[62vh]">
        <DataTable
          columns={columns as unknown as DataTableColumns}
          data={data as unknown as { id: string | number }[]}
          height="flex-1"
          onTableReady={(ready) => setTable(ready as unknown as Table<Row>)}
          paginationKey={paginationKey}
          filters={filters}
          filterSchema={schema}
          globalFilter={searchQuery}
          emptyMessage={emptyMessage}
          enableRowSelection={false}
          scrollbarClassName="overflow-auto scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400 scrollbar-track-transparent"
        />
      </div>
    </div>
  )
}

// A filterable table for one Portfolio tab (Filters bar, Search, AddFilter, export), backed by the
// shared filter engine and DataTable. Its own FiltersProvider keeps each tab's facets separate.
export const PortfolioTable = <Row,>({
  contextId,
  ...props
}: PortfolioTableProps<Row>) => (
  <FiltersProvider contextId={contextId}>
    <PortfolioTableInner {...props} />
  </FiltersProvider>
)
